import React, {
    useCallback, useEffect, useMemo, useState, FC,
} from 'react';
import { useTranslation } from 'react-i18next';

// Library / Components
import { Infobox } from 'ui-library';
import EmptyContent from 'components/EmptyContent';
import Preloader from 'components/Preloader';
import QuestionnaireGroup from 'components/QuestionnaireGroup';

// Hooks & Utils
import { useCreatePortfolioSelectors, useCreatePortfolioState } from 'datasource/CreatePortfolio';
import { usePortfolioProcessNavigation } from 'hooks/helpers/usePortfolioProcessNavigation';
import { useClientProfile } from 'prodivers/clientProfile';
import { removeNonUniqueEntries } from 'utils/formatting';
import handlerRequestCanceling from 'utils/handlerRequestCanceling';
import HandlerError from 'errors/HandlerError';

// Adaptors
import { adaptAnswers } from 'adaptors/adaptAnswers';
import { adaptQualityCheckQuestionnaire } from 'widgets/QualityCheck/adapters/adaptQuestionnaire';

// Local Logic
import { formDataSelector, questionnaireSelector } from './selectors';
import { useSelector } from './useSelector';
import useQualityCheckQuestionnaire from './hooks/useQualityCheckQuestionnaire';

// --- Interfaces ---

interface Question {
    Id: number;
    Type: string;
    Value?: {
        Ids?: number[];
    };
    Answers: Array<{ Id: number }>;
}

interface Step {
    Questions: Question[];
}

interface Group {
    Id: number;
    Name: string;
    Steps: Step[];
}

const QualityCheck: FC = () => {
    const [isSubmitted, setIsSubmitted] = useState<boolean>(false);
    const [isSending, setSending] = useState<boolean>(false);
    const { validateQuestionnaire } = useQualityCheckQuestionnaire();

    const { t } = useTranslation();

    // Client Provider
    const information = useClientProfile();
    const {
        currentInvestmentApplicationId,
        applications,
    } = useCreatePortfolioState();

    const {
        isAdvisoryProcessSelector,
        is3bProductGroupSelector,
        containerIdSelector,
        selectedProductSelector,
        isJointAccountSelector,
        targetInvestorProfileClientSelector,
        targetInvestorProfileOtherClientSelector,
    } = useCreatePortfolioSelectors();

    const [pageErrors, setPageErrors] = useState<any>(undefined);

    // Selectors
    const {
        data, isLoading, getQuestionnaire,
        isLoadingPost, postQuestionnaire, error: postuseQuestionnaireError,
    } = useSelector(questionnaireSelector);

    const qualityCheckProfileGroup = data?.Groups?.[1];
    const groupId = qualityCheckProfileGroup?.Id;

    const {
        data: formData, initializeData, saveData, isInitDone,
    } = useSelector(formDataSelector);

    // Mappings
    const answersToValidate = useMemo(() => {
        if (groupId) return formData?.[groupId] || {};

        return Object.values(formData || {}).reduce((acc, group) => ({ ...acc, ...group }), {});
    }, [formData, groupId]);

    const financialServiceQuestion = useMemo(
        () => qualityCheckProfileGroup?.Steps?.[0].Questions?.find(
            ({ Type }) => (Type === 'SingleAnswer'),
        ),
        [qualityCheckProfileGroup?.Steps?.[0].Questions],
    );

    const isOthersSelected = answersToValidate?.[238]?.includes(766);

    const filteredQuestionnaireBasedOnProduct = useMemo(() => {
        const questionsToHide: number[] = [];

        if (!isOthersSelected) {
            questionsToHide.push(258);
        }

        const productKey = selectedProductSelector?.settings?.productKey;

        if (['3a', 'FZ'].includes(productKey)) {
            questionsToHide.push(259, 261);
        }
        if (['FZP', '3ap'].includes(productKey)) {
            questionsToHide.push(259, 260, 261);
        }

        if (is3bProductGroupSelector) {
            const questionsToExcludeFor3b: Record<string, number[]> = {
                ZIFD: [259, 261],
                ZIVV: [260, 261],
                AZP: [260, 261],
                AZPI: [260, 261],
                AZPD: [260, 261],
                ZIC: [259, 260],
                PK: [259, 260, 261],
            };

            const validApplications = Object.keys(applications || {})?.filter(
                app => app !== 'undefined' && !applications?.[app]?.changePlan,
            );

            const questionIdsToHide = validApplications?.map(key => questionsToExcludeFor3b[applications?.[key]?.investmentDescription?.productExternalId])?.flat();

            if (questionIdsToHide) {
                questionsToHide.push(...removeNonUniqueEntries(questionIdsToHide, validApplications?.length));
            }
        }

        // Deep clone data to avoid mutation
        const newData: Group = data?.Groups?.[1] ? JSON.parse(JSON.stringify(data.Groups[1])) : null;

        if (newData?.Steps) {
            newData.Steps.forEach(step => {
                step.Questions = step?.Questions?.filter(question => !questionsToHide?.includes(question?.Id));
            });
        }

        return newData;
    }, [data?.Groups, selectedProductSelector, applications, is3bProductGroupSelector, isOthersSelected]);

    const { isValid: allQuestionsFilled, errors } = useMemo(
        () => validateQuestionnaire({ data: filteredQuestionnaireBasedOnProduct, answersToValidate }),
        [answersToValidate, validateQuestionnaire, filteredQuestionnaireBasedOnProduct],
    );

    // Effects
    useEffect(() => {
        if (containerIdSelector) {
            getQuestionnaire({
                id: is3bProductGroupSelector ? containerIdSelector : currentInvestmentApplicationId,
                isContainer: is3bProductGroupSelector,
                adapter: adaptQualityCheckQuestionnaire,
            });
        }
    }, [getQuestionnaire, currentInvestmentApplicationId, containerIdSelector, is3bProductGroupSelector]);

    useEffect(() => {
        if (!formData && data?.Groups?.length) {
            const groupIds = data.Groups.map(({ Id }: { Id: number }) => Id);

            initializeData({ groupIds });
        }
    }, [data, formData, initializeData]);

    useEffect(() => {
        if (isInitDone && groupId && financialServiceQuestion) {
            const questions: any[] = [];

            if (!financialServiceQuestion?.Value?.Ids?.length) {
                const answerIdToSelect = isAdvisoryProcessSelector ? 1 : 0;

                questions.push({
                    questionId: financialServiceQuestion?.Id,
                    value: financialServiceQuestion?.Answers[answerIdToSelect].Id,
                });
            }

            if (questions?.length) {
                saveData({ groupId, questions });
            }
        }
    }, [isInitDone, groupId, isAdvisoryProcessSelector, financialServiceQuestion, saveData]);

    // Handlers
    const onContinue = useCallback(async () => {
        setIsSubmitted(true);

        if (allQuestionsFilled && groupId) {
            setSending(true);
            try {
                if (!isOthersSelected) {
                    // Update formData reference locally if needed before adaptation
                    if (formData[groupId]) formData[groupId][258] = null;
                }
                const answers = adaptAnswers({ data, values: formData });

                await postQuestionnaire({
                    answers,
                    id: is3bProductGroupSelector ? containerIdSelector : currentInvestmentApplicationId,
                    isContainer: is3bProductGroupSelector,
                });
                stepComplete();
            } catch (err) {
                handlerRequestCanceling(
                    HandlerError({
                        setError: setPageErrors,
                        setLoading: setSending,
                    }),
                )(err);
                setPageErrors(undefined);
            } finally {
                setSending(false);
                setIsSubmitted(false);
            }
        }
    }, [
        allQuestionsFilled, data, formData, postQuestionnaire, is3bProductGroupSelector,
        containerIdSelector, currentInvestmentApplicationId, groupId, isOthersSelected,
    ]);

    const onChange = useCallback(({ questionId, value }: { questionId: number, value: any }) => {
        if (groupId) {
            saveData({ groupId, questions: [{ questionId, value }] });
        }
    }, [groupId, saveData]);

    useEffect(() => {
        if (postuseQuestionnaireError) {
            setPageErrors(postuseQuestionnaireError);
        }
    }, [postuseQuestionnaireError]);

    const { stepComplete } = usePortfolioProcessNavigation({
        saveOnContinue: onContinue,
        enableAutoStepComplete: false,
        navigationDisabled: information?.isLoading || isLoading || isLoadingPost || isSending,
        continueInProgress: isSending,
        pageErrors,
    });

    const isPageLoading = information?.isLoading || isLoading || isLoadingPost || isSending;

    return (
        <Preloader isLoading={isPageLoading}>
            <div className="new-portfolio-content">
                <EmptyContent data={data} text={t('advisoryDashboard.noData')}>
                    <QuestionnaireGroup
                        data={filteredQuestionnaireBasedOnProduct}
                        title={data?.Groups?.[1]?.Name}
                        value={groupId ? formData?.[groupId] || {} : {}}
                        errors={isSubmitted ? errors : {}}
                        onChange={onChange}
                    />
                </EmptyContent>

                {isJointAccountSelector && (
                    <Infobox className="ja-message">
                        {t('portfolio.createNewPortfolio.QualityCheck.jaInfoMessage', {
                            clientFullName: targetInvestorProfileOtherClientSelector?.name,
                            clientOwnerFullName: targetInvestorProfileClientSelector?.name,
                        })}
                    </Infobox>
                )}
            </div>
        </Preloader>
    );
};

export default QualityCheck;
